import type { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateLocationAndHoursForShift } from "@/components/NormalizedNurseMenu/useUpdateLocationAndHours";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { useNurseTimeOff } from "@/hooks/useNurseTimeOff";
import { useRosterLookup } from "@/hooks/useRosterLookup";
import { SheetStep } from "@/ui/Popups/SheetStep";
import { SheetStepButton } from "@/ui/Popups/SheetStepButton";
import { isInPTO } from "@/utils/dateUtils";
import { NurseFullRoleShort, type NurseInShift } from "@/utils/models";
import { useShiftPredictionWarnings } from "@/utils/warningDialogs/useShiftPredictionWarnings";
import { isAgencyNurse } from "@/utils/warningDialogs/utils";
import { WarningDialog } from "@/utils/warningDialogs/WarningDialog";
import { GapsSheetHoursPicker } from "./GapsSheetHoursPicker";
import { GapsStaffStep } from "./gapsSheetTypes";

type Props = {
	nurseInShift: NurseInShift;
	name: string;
	/** The person's hours as the row prints them, for the heading. */
	hoursLabel: string;
	onStep: (step: GapsStaffStep) => void;
	onClose: () => void;
};

const SHEET = "gaps-change-hours";

type Hours = { start: DateTime; end: DateTime };

/** How the hours read in a confirmation — the same 12-hour form the picker's own chips use. */
const HOURS_FORMAT = "h:mm a";

/**
 * New hours for this shift (P3-5), gated exactly as the desktop editor gates them: the shift's own time picker
 * (`GapsSheetHoursPicker`, the desktop editor inline, streaming every valid change up), then `useShiftPredictionWarnings` with the shift's
 * own id excluded, so its current hours are not counted twice against the new ones — PTO, agency, overtime and
 * daily-hours warnings in the desktop's order, each a `WarningDialog` that covers the bottom bar (P3-13).
 *
 * PTO rides the same cascade (`hasPto`) rather than a dialog of its own: the desktop editor checks it in a local
 * dialog first only because its cascade predates PTO support; the predicate is the editor's own `isInPTO`.
 */
export const GapsStaffHoursStep: FC<Props> = ({ nurseInShift, name, hoursLabel, onStep, onClose }) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { chosenDepartment } = useHospitalData();
	const { getNurseById } = useRosterLookup(chosenDepartment.department_id);
	const timeOffOf = useNurseTimeOff();
	const { updateLocationAndHours, updateNurseLocationOrTimesLoading } = useUpdateLocationAndHoursForShift();
	const [draft, setDraft] = useState<Hours>({ start: nurseInShift.startTime, end: nurseInShift.endTime });
	const [isChecking, setIsChecking] = useState<boolean>(false);
	// The hours waiting on the warning cascade. A ref, not state: the cascade can resolve in the same tick as the tap
	// (no warnings), before a state write would have re-rendered — the reason the desktop menu holds it the same way.
	const pendingRef = useRef<Hours | null>(null);

	const analyticsBase = useMemo(
		() => ({ department_id: chosenDepartment.department_id, shift_id: nurseInShift.id }),
		[chosenDepartment.department_id, nurseInShift.id],
	);

	const performSave = useCallback(async (): Promise<void> => {
		const pending = pendingRef.current;
		pendingRef.current = null;
		if (pending === null) return;
		const saved = await updateLocationAndHours({
			originalNurseInShift: nurseInShift,
			updatedNurseInShift: { ...nurseInShift, startTime: pending.start, endTime: pending.end },
		});
		if (!saved) {
			// The Save button only enables on changed hours, so `false` is a failed write, already in the error snackbar.
			trackError("gaps_staff_hours_change_error", analyticsBase);
			return;
		}
		track("gaps_staff_hours_change_success", analyticsBase);
		showSnackbar(
			t("gaps.staffSheet.hoursSaved", { name, hours: `${pending.start.toFormat(HOURS_FORMAT)} – ${pending.end.toFormat(HOURS_FORMAT)}` }),
			"success",
		);
		onClose();
	}, [analyticsBase, name, nurseInShift, onClose, showSnackbar, t, track, trackError, updateLocationAndHours]);

	const {
		warningState,
		runChecks,
		handleAgencyConfirm,
		handleOvertimeConfirm,
		handleDailyHoursConfirm,
		handleCancelWarnings,
		handlePtoConfirm,
		longShiftDuration,
	} = useShiftPredictionWarnings(() => void performSave());

	const isChanged = !draft.start.equals(nurseInShift.startTime) || !draft.end.equals(nurseInShift.endTime);

	const handleSave = useCallback(async (): Promise<void> => {
		const nurse = getNurseById(nurseInShift.nurseId);
		const hasPto = timeOffOf(nurseInShift.nurseId).some((preference) => isInPTO(preference, draft.start, chosenDepartment.dayStart, draft.end));
		pendingRef.current = draft;
		track("gaps_staff_hours_change_start", { ...analyticsBase, has_pto: hasPto });
		setIsChecking(true);
		await runChecks({
			nurseId: nurseInShift.nurseId,
			startTime: draft.start,
			endTime: draft.end,
			isGlobalSalary: nurse?.isGlobalSalary,
			isAgency: isAgencyNurse(nurseInShift.type),
			hasPto,
			originalShiftId: nurseInShift.id,
		});
		setIsChecking(false);
	}, [analyticsBase, chosenDepartment.dayStart, draft, getNurseById, timeOffOf, nurseInShift, runChecks, track]);

	const handleCancelWarning = useCallback((): void => {
		pendingRef.current = null;
		track("gaps_staff_hours_change_cancelled", analyticsBase);
		handleCancelWarnings();
	}, [analyticsBase, handleCancelWarnings, track]);

	const handleTimeChange = useCallback(({ newStartTime, newEndTime }: { newStartTime: DateTime; newEndTime: DateTime }) => {
		setDraft({ start: newStartTime, end: newEndTime });
	}, []);

	return (
		<>
			<SheetStep
				stepKey={GapsStaffStep.Hours}
				dataTestId={SHEET}
				title={t("gaps.staffSheet.hoursTitle")}
				subtitle={`${name} · ${hoursLabel}`}
				footer={
					<>
						<SheetStepButton tone="dismiss" onClick={() => onStep(GapsStaffStep.Actions)} dataTestId={`${SHEET}-cancel`}>
							{t("back")}
						</SheetStepButton>
						<SheetStepButton
							tone="confirm"
							onClick={() => void handleSave()}
							disabled={!isChanged}
							loading={isChecking || updateNurseLocationOrTimesLoading}
							dataTestId={`${SHEET}-confirm`}
						>
							{t("gaps.staffSheet.saveHours")}
						</SheetStepButton>
					</>
				}
			>
				<GapsSheetHoursPicker
					startTime={nurseInShift.startTime}
					endTime={nurseInShift.endTime}
					onTimeChange={handleTimeChange}
					role={NurseFullRoleShort[nurseInShift.role]}
					jobId={nurseInShift.jobId}
					dataTestId={`${SHEET}-picker`}
				/>
			</SheetStep>
			<WarningDialog
				openPtoWarning={warningState.isPtoWarningOpen}
				openAgencyWarning={warningState.isAgencyWarningOpen}
				openOvertimeWarning={warningState.isOvertimeWarningOpen}
				openDailyHoursWarning={warningState.isDailyHoursWarningOpen}
				overtimeDetails={warningState.overtimeDetails}
				dailyHoursDetails={warningState.dailyHoursDetails}
				longShiftDuration={longShiftDuration}
				onCancelPto={handleCancelWarning}
				onConfirmPto={handlePtoConfirm}
				onCancelAgency={handleCancelWarning}
				onConfirmAgency={handleAgencyConfirm}
				onCancelOvertime={handleCancelWarning}
				onConfirmOvertime={handleOvertimeConfirm}
				onCancelDailyHours={handleCancelWarning}
				onConfirmDailyHours={handleDailyHoursConfirm}
			/>
		</>
	);
};
